import { FC, JSX, useEffect, useState } from 'react';
import SwiperCore, { A11y, EffectFade, SwiperOptions } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import classNames from 'classnames';

import { replaceWhiteSpacesWithUnderscore } from '../../../utils/strings';
import { useIsInEditorMode } from '../../../utils/use-is-in-editor-mode';

SwiperCore.use([A11y, EffectFade]);

interface TabsContentProps {
  autoHeight?: boolean;
  isInEditor?: boolean;
  childComponents: JSX.Element[];
  activeItemId: number;
  effect?: SwiperOptions['effect'];
}

const TABS_AUTO_HEIGHT_SPEED = 300;

export const TabsContent: FC<TabsContentProps> = ({
  autoHeight,
  isInEditor,
  childComponents,
  activeItemId,
  effect,
}) => {
  const [swiper, setSwiper] = useState<SwiperCore | null>();
  const { isInEditorMode } = useIsInEditorMode(isInEditor);

  useEffect(() => {
    swiper?.slideTo(Number(activeItemId));
  }, [activeItemId, swiper]);

  if (isInEditorMode) {
    return (
      <>
        {childComponents.map((item, index) => {
          const key = `tab-content-${index}`;
          return (
            <div
              className={classNames('cmp-tabs__author-tab-content snowflake-tabs-author-content', {
                active: activeItemId === index,
              })}
              key={key}
            >
              {childComponents[index]}
            </div>
          );
        })}
      </>
    );
  }

  const onSlideChangeTransitionEnd = (swiperInstance: SwiperCore) => {
    if (autoHeight) {
      swiperInstance.updateAutoHeight(TABS_AUTO_HEIGHT_SPEED);
    }
  };

  return (
    <div data-testid="content" className="snowflake-tabs-published-content">
      <Swiper
        effect={effect}
        speed={1000}
        allowTouchMove={false}
        onSwiper={(swiperCore: SwiperCore) => {
          setSwiper(swiperCore);
        }}
        fadeEffect={{ crossFade: true }}
        spaceBetween={1}
        autoHeight={autoHeight}
        observer={autoHeight}
        observeParents={autoHeight}
        onSlideChangeTransitionEnd={onSlideChangeTransitionEnd}
      >
        {childComponents.map((childComponent) => {
          return (
            <SwiperSlide
              data-testing={replaceWhiteSpacesWithUnderscore(
                childComponent.props['cq:panelTitle']
              )?.toLocaleLowerCase()}
              tag="span"
              key={childComponent.key}
            >
              <div className="snowflake-tabs-published-content-slide-content" role="tabpanel">
                {childComponent}
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
};
